import { useEffect, useState } from "react";
import { Globe, Copy, Crown, CalendarBlank, Plus, Trash, Envelope, Phone, ChatCircleText, ArrowSquareOut, Clock, User, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";
import { fmtDate, ONBOARDING_BASE } from "../lib/helpers";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

function leadLanderUrl(id) {
  return `${ONBOARDING_BASE}/property/${id}`;
}

function OpenHousesEditor({ p }) {
  const [rows, setRows] = useState(p.open_houses || []);
  const [saving, setSaving] = useState(false);
  const add = () => setRows([...rows, { date: "", start: "", end: "" }]);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const del = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const clean = rows.filter((r) => r.date);
      const r = await client.put(`/properties/${p.id}/open-houses`, { open_houses: clean });
      setRows(r.data.open_houses);
      toast.success("Open houses updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save open houses"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-2" data-testid="ll-openhouse-editor">
      {rows.length === 0 && <p className="text-xs text-[#5C5A58]">No open houses scheduled yet.</p>}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="date" data-testid={`ll-oh-date-${i}`} value={r.date || ""} onChange={(e) => upd(i, "date", e.target.value)}
            className="flex-1 px-2 py-1.5 rounded-md border border-[#E9DDC6] text-sm" />
          <input type="time" data-testid={`ll-oh-start-${i}`} value={r.start || ""} onChange={(e) => upd(i, "start", e.target.value)}
            className="px-2 py-1.5 rounded-md border border-[#E9DDC6] text-sm" />
          <input type="time" data-testid={`ll-oh-end-${i}`} value={r.end || ""} onChange={(e) => upd(i, "end", e.target.value)}
            className="px-2 py-1.5 rounded-md border border-[#E9DDC6] text-sm" />
          <button onClick={() => del(i)} data-testid={`ll-oh-del-${i}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash size={16} /></button>
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={add} data-testid="ll-oh-add" className="border-[#E9DDC6]"><Plus size={15} className="mr-1" />Add slot</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="ll-oh-save" className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save open houses"}</Button>
      </div>
    </div>
  );
}

function TourSlotsEditor({ p }) {
  const [rows, setRows] = useState(p.tour_slots || []);
  const [saving, setSaving] = useState(false);
  const add = () => setRows([...rows, { date: "", start: "", end: "", status: "open" }]);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const del = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const clean = rows.filter((r) => r.date);
      const r = await client.put(`/properties/${p.id}/tour-slots`, { tour_slots: clean });
      setRows(r.data.tour_slots);
      toast.success("Tour availability updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save tour slots"); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-2" data-testid="ll-tourslot-editor">
      <p className="text-xs text-[#5C5A58]">Post time slots buyers can book directly from the public page.</p>
      {rows.length === 0 && <p className="text-xs text-[#8A8782]">No tour times yet.</p>}
      {rows.map((r, i) => {
        const booked = r.status === "booked";
        return (
          <div key={r.id || i} className={`flex items-center gap-2 ${booked ? "opacity-90" : ""}`}>
            <input type="date" data-testid={`ll-ts-date-${i}`} value={r.date || ""} disabled={booked} onChange={(e) => upd(i, "date", e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-md border border-[#E9DDC6] text-sm disabled:bg-[#FAF9F6]" />
            <input type="time" data-testid={`ll-ts-start-${i}`} value={r.start || ""} disabled={booked} onChange={(e) => upd(i, "start", e.target.value)}
              className="px-2 py-1.5 rounded-md border border-[#E9DDC6] text-sm disabled:bg-[#FAF9F6]" />
            <input type="time" data-testid={`ll-ts-end-${i}`} value={r.end || ""} disabled={booked} onChange={(e) => upd(i, "end", e.target.value)}
              className="px-2 py-1.5 rounded-md border border-[#E9DDC6] text-sm disabled:bg-[#FAF9F6]" />
            {booked && <Badge className="bg-[#2E7D5B]/12 text-[#2E7D5B] border-[#2E7D5B]/30 text-[10px]">Booked</Badge>}
            <button onClick={() => del(i)} data-testid={`ll-ts-del-${i}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash size={16} /></button>
          </div>
        );
      })}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={add} data-testid="ll-ts-add" className="border-[#E9DDC6]"><Plus size={15} className="mr-1" />Add tour slot</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="ll-ts-save" className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save tour times"}</Button>
      </div>
    </div>
  );
}

function LeadsList({ p, onCount }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | new | tour | message
  const load = async () => {
    try {
      const r = await client.get(`/properties/${p.id}/leads`);
      setLeads(r.data.leads);
      onCount && onCount(r.data.new_count ?? r.data.leads.filter((l) => l.status === "new").length);
    } catch (e) { /* gated */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [p.id]);
  const setStatus = async (lid, status) => {
    try {
      const r = await client.patch(`/leads/${lid}`, { status });
      const next = leads.map((l) => (l.id === lid ? r.data : l));
      setLeads(next);
      onCount && onCount(next.filter((l) => l.status === "new").length);
    } catch (e) { toast.error("Could not update"); }
  };

  const counts = {
    all: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    tour: leads.filter((l) => l.kind === "tour").length,
    message: leads.filter((l) => l.kind !== "tour").length,
  };
  const shown = leads.filter((l) =>
    filter === "all" ? true : filter === "new" ? l.status === "new" : l.kind === (filter === "tour" ? "tour" : "message") || (filter === "message" && l.kind !== "tour"));

  if (loading) return <p className="text-xs text-[#5C5A58]">Loading inquiries…</p>;
  if (leads.length === 0) return (
    <div className="text-center py-10 text-[#5C5A58]" data-testid="ll-no-leads">
      <User size={34} weight="duotone" className="mx-auto mb-2 text-[#B08D57]" />
      <p className="text-sm">No leads yet.</p>
      <p className="text-xs text-[#8A8782] mt-0.5">Share your LeadLander link — inquiries and tour requests land here.</p>
    </div>
  );

  const FILTERS = [["all", "All", counts.all], ["new", "New", counts.new], ["tour", "Tours", counts.tour], ["message", "Messages", counts.message]];
  return (
    <div className="space-y-3" data-testid="ll-leads-list">
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map(([v, label, n]) => (
          <button key={v} data-testid={`ll-filter-${v}`} onClick={() => setFilter(v)}
            className={`px-2.5 py-1 rounded-full text-xs border transition ${filter === v ? "bg-[#4A0000] text-white border-[#4A0000]" : "bg-white text-[#5C5A58] border-[#E9DDC6] hover:border-[#B08D57]"}`}>
            {label}<span className={`ml-1 ${filter === v ? "text-white/70" : "text-[#9A968E]"}`}>{n}</span>
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {shown.map((l) => (
          <div key={l.id} className={`rounded-md border p-3 ${l.status === "new" ? "border-[#B08D57]/50 bg-[#B08D57]/[0.08]" : "border-[#E9DDC6] bg-white"}`} data-testid={`ll-lead-${l.id}`}>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#4A0000]/8 text-[#4A0000] shrink-0">
                {l.kind === "tour" ? <CalendarBlank size={14} weight="fill" /> : <ChatCircleText size={14} weight="fill" />}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1A1A1A] truncate">{l.name || "Website visitor"}</p>
                <p className="text-[11px] text-[#8A8782]">{l.kind === "tour" ? "Tour request" : "Message"} · {fmtDate(l.created_at)}</p>
              </div>
              <Badge className={`ml-auto text-[10px] capitalize ${l.status === "new" ? "bg-[#4A0000] text-white" : "bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]"}`}>{l.status}</Badge>
            </div>
            {l.kind === "tour" && l.tour_date && (
              <p className="text-xs text-[#8A6D1B] mt-2 flex items-center gap-1.5"><Clock size={12} weight="fill" />{fmtDate(l.tour_date)}{l.tour_time ? ` at ${l.tour_time}` : ""}</p>
            )}
            {l.message && <p className="text-sm text-[#1A1A1A] mt-2 leading-snug">“{l.message}”</p>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
              {l.email && <a href={`mailto:${l.email}`} data-testid={`ll-lead-email-${l.id}`} className="flex items-center gap-1 text-[#4A0000] hover:underline"><Envelope size={13} weight="fill" />{l.email}</a>}
              {l.phone && <a href={`tel:${l.phone}`} data-testid={`ll-lead-phone-${l.id}`} className="flex items-center gap-1 text-[#4A0000] hover:underline"><Phone size={13} weight="fill" />{l.phone}</a>}
              {!l.email && !l.phone && <span className="text-[#9A968E]">No contact info left</span>}
            </div>
            <div className="flex gap-2 mt-2.5">
              {l.status !== "read" && <button onClick={() => setStatus(l.id, "read")} data-testid={`ll-lead-read-${l.id}`} className="text-xs px-2 py-1 rounded border border-[#E9DDC6] hover:bg-[#FAF9F6]">Mark read</button>}
              {l.status !== "archived" && <button onClick={() => setStatus(l.id, "archived")} data-testid={`ll-lead-archive-${l.id}`} className="text-xs px-2 py-1 rounded border border-[#E9DDC6] hover:bg-[#FAF9F6]">Archive</button>}
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-xs text-[#8A8782] py-4 text-center">No {filter} leads.</p>}
      </div>
    </div>
  );
}

export default function LeadLanderPanel({ p, patch, isAdmin, isOwnerAgent, penthouse }) {
  const url = leadLanderUrl(p.id);
  const canManage = isAdmin || (isOwnerAgent && penthouse);
  const [newCount, setNewCount] = useState(0);
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("LeadLander link copied"); };

  const trigger = "flex-1 min-w-0 justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-[#4A0000] data-[state=active]:shadow-sm text-[#5C5A58] rounded-md px-2 sm:px-3 py-1.5 text-[11px] sm:text-sm";

  return (
    <div className="border border-[#B08D57]/40 rounded-md bg-[#B08D57]/[0.06] p-4 space-y-4" data-testid="leadlander-panel">
      <div className="flex items-center gap-2">
        <Globe size={16} weight="duotone" color="#4A0000" />
        <span className="text-sm font-medium text-[#4A0000]">LeadLander Page</span>
        <Badge className="ml-auto bg-[#B08D57]/15 text-[#8A6D1B] border-[#B08D57]/40 flex items-center gap-1"><Crown size={11} weight="fill" />Partner Perk</Badge>
      </div>

      {/* Share link — available to admin + the listing agent */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <input readOnly value={url} data-testid="ll-share-url" className="flex-1 min-w-0 px-3 py-2 rounded-md border border-[#E9DDC6] text-xs bg-white text-[#5C5A58]" />
          <Button variant="outline" size="sm" onClick={copy} data-testid="ll-copy" className="border-[#E9DDC6] shrink-0"><Copy size={15} className="mr-1" />Copy</Button>
        </div>
        <a href={url} target="_blank" rel="noreferrer" data-testid="ll-open" className="inline-flex w-full sm:w-auto justify-center items-center gap-1 rounded-md border border-[#4A0000] text-[#4A0000] hover:bg-[#4A0000] hover:text-white transition-colors px-3 py-2 text-sm"><ArrowSquareOut size={15} />View</a>
      </div>

      {/* Non-partner agent upsell */}
      {isOwnerAgent && !penthouse && (
        <div className="rounded-md bg-white border border-[#E4D08A] p-3" data-testid="ll-upsell">
          <p className="text-sm text-[#8A6D1B] flex items-center gap-1.5"><Crown size={15} weight="fill" />Become a Penthouse Partner to receive leads, set open houses and manage tour requests from this page.</p>
        </div>
      )}

      {/* Management tabs — managers only */}
      {canManage && (
        <Tabs defaultValue="leads" className="w-full pt-1" data-testid="ll-tabs">
          <TabsList className="bg-[#F1EBDD] w-full justify-start h-auto p-1 gap-1">
            <TabsTrigger value="leads" data-testid="ll-tab-leads" className={trigger}>
              <ChatCircleText size={14} weight="fill" className="mr-1.5 shrink-0" />Leads
              {newCount > 0 && <span className="ml-1.5 min-w-4 h-4 px-1 rounded-full bg-[#B08D57] text-[#1A1715] text-[10px] font-bold inline-flex items-center justify-center">{newCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="openhouses" data-testid="ll-tab-openhouses" className={trigger}><CalendarBlank size={14} weight="fill" className="mr-1.5 shrink-0" />Open Houses</TabsTrigger>
            <TabsTrigger value="tours" data-testid="ll-tab-tours" className={trigger}><Clock size={14} weight="fill" className="mr-1.5 shrink-0" />Tour Times</TabsTrigger>
            {isAdmin && <TabsTrigger value="page" data-testid="ll-tab-page" className={trigger}><PencilSimple size={14} weight="fill" className="mr-1.5 shrink-0" />Page</TabsTrigger>}
          </TabsList>

          <TabsContent value="leads" className="mt-3">
            <LeadsList p={p} onCount={setNewCount} />
          </TabsContent>

          <TabsContent value="openhouses" className="mt-3">
            <OpenHousesEditor p={p} />
          </TabsContent>

          <TabsContent value="tours" className="mt-3">
            <TourSlotsEditor p={p} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="page" className="mt-3">
              <div className="space-y-2" data-testid="ll-content-editor">
                <p className="text-xs text-[#5C5A58]">Public page content — only you (admin) can edit this.</p>
                <input type="text" defaultValue={p.lead_headline || ""} key={`llh-${p.id}`} placeholder="Headline (defaults to property title)"
                  data-testid="ll-edit-headline"
                  onBlur={(e) => { if ((e.target.value || "") !== (p.lead_headline || "")) patch({ lead_headline: e.target.value }); }}
                  className="w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm" />
                <textarea rows={3} defaultValue={p.lead_description || ""} key={`lld-${p.id}`} placeholder="Public description of the home…"
                  data-testid="ll-edit-description"
                  onBlur={(e) => { if ((e.target.value || "") !== (p.lead_description || "")) patch({ lead_description: e.target.value }); }}
                  className="w-full px-3 py-2 rounded-md border border-[#E9DDC6] text-sm" />
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" defaultValue={p.list_price ?? ""} key={`llp-${p.id}`} placeholder="List price $"
                    data-testid="ll-edit-price"
                    onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (p.list_price ?? null)) patch({ list_price: v }); }}
                    className="px-3 py-2 rounded-md border border-[#E9DDC6] text-sm" />
                  <input type="number" defaultValue={p.beds ?? ""} key={`llb-${p.id}`} placeholder="Beds"
                    data-testid="ll-edit-beds"
                    onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (p.beds ?? null)) patch({ beds: v }); }}
                    className="px-3 py-2 rounded-md border border-[#E9DDC6] text-sm" />
                  <input type="number" step="0.5" defaultValue={p.baths ?? ""} key={`llba-${p.id}`} placeholder="Baths"
                    data-testid="ll-edit-baths"
                    onBlur={(e) => { const v = e.target.value === "" ? null : Number(e.target.value); if (v !== (p.baths ?? null)) patch({ baths: v }); }}
                    className="px-3 py-2 rounded-md border border-[#E9DDC6] text-sm" />
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}
